/**
 * ModelView - 3D dollhouse/model viewing mode
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export class ModelView {
  constructor(scene, camera, renderer, model, config) {
    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;
    this.model = model;
    this.config = config;
    
    this.isActive = false;
    this.hotspots = [];
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    this.hoveredHotspot = null;
    
    // Track mouse movement to distinguish clicks from drags
    this.mouseDownPosition = new THREE.Vector2();
    this.isDragging = false;
    
    // Calculate model's visual center for better camera positioning
    const box = new THREE.Box3().setFromObject(model);
    this.modelCenter = box.getCenter(new THREE.Vector3());
    this.modelHeight = box.max.y - box.min.y;
    
    console.log(`📏 Model bounds: min=(${box.min.x.toFixed(2)}, ${box.min.y.toFixed(2)}, ${box.min.z.toFixed(2)}), max=(${box.max.x.toFixed(2)}, ${box.max.y.toFixed(2)}, ${box.max.z.toFixed(2)})`);
    console.log(`🎯 Model center: (${this.modelCenter.x.toFixed(2)}, ${this.modelCenter.y.toFixed(2)}, ${this.modelCenter.z.toFixed(2)})`);
    console.log(`📐 Model height: ${this.modelHeight.toFixed(2)}`);
    
    // Initialize orbit controls
    this.controls = new OrbitControls(camera, renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.rotateSpeed = 0.7;
    this.controls.panSpeed = 0.5;
    this.controls.zoomSpeed = 1.0;
    this.controls.minDistance = 1;
    this.controls.maxDistance = 10;
    this.controls.maxPolarAngle = Math.PI / 2; // Don't go below floor
    this.controls.enabled = false;
    
    this.onEnterPanoramaCallback = null;
    
    this.setupEventListeners();
  }

  /**
   * Enter model view
   * @param {object} options - Optional settings
   * @param {number} options.fromRotY - Viewing direction from panorama (degrees)
   */
  enter(options = {}) {
    console.log('🏠 Entering model view');
    console.log(`   isActive before: ${this.isActive}`);
    
    this.isActive = true;
    
    // Clean up any navigation hotspots that might be leftover in the scene
    // (These are floor circles from panorama view that should not be visible here)
    const hotspotsToRemove = [];
    this.scene.children.forEach(child => {
      if (child.name && child.name.startsWith('hotspot_')) {
        hotspotsToRemove.push(child);
      }
    });
    hotspotsToRemove.forEach(child => {
      console.log(`🧹 Removing leftover navigation hotspot: ${child.name}`);
      this.scene.remove(child);
    });
    
    if (hotspotsToRemove.length > 0) {
      console.log(`✅ Removed ${hotspotsToRemove.length} leftover navigation hotspots from scene`);
    }
    
    // Reset scene background
    this.scene.background = new THREE.Color(0x1a1a2e);
    console.log('   Scene background set to 0x1a1a2e');
    
    // Make model visible
    this.makeModelVisible();
    console.log('   Model visibility restored');
    
    // Position camera based on viewing direction
    const distance = 5; // Distance from model center
    const targetHeight = this.modelCenter.y; // Look at model's vertical center
    const cameraHeight = targetHeight + 0.5; // Slightly above center for better view
    
    if (options.fromRotY !== undefined) {
      // Position camera to maintain viewing direction from panorama
      const angleRad = THREE.MathUtils.degToRad(options.fromRotY);
      const x = Math.sin(angleRad) * distance;
      const z = Math.cos(angleRad) * distance;
      
      this.camera.position.set(x, cameraHeight, z);
      console.log(`   Camera positioned from panorama direction: rotY=${options.fromRotY.toFixed(1)}° → (${x.toFixed(2)}, ${cameraHeight.toFixed(2)}, ${z.toFixed(2)})`);
    } else {
      // Default camera position
      this.camera.position.set(3, cameraHeight, 3);
      console.log(`   Camera positioned at default: (3, ${cameraHeight.toFixed(2)}, 3)`);
    }
    
    // Look at model's visual center, not the floor
    this.camera.lookAt(this.modelCenter.x, targetHeight, this.modelCenter.z);
    console.log(`   Camera target: (${this.modelCenter.x.toFixed(2)}, ${targetHeight.toFixed(2)}, ${this.modelCenter.z.toFixed(2)})`);
    
    // Reset camera rotation to default
    this.camera.rotation.order = 'XYZ';
    console.log('   Camera rotation order set to XYZ');
    
    // Enable orbit controls with target at model center
    this.controls.enabled = true;
    this.controls.target.set(this.modelCenter.x, targetHeight, this.modelCenter.z);
    this.controls.update();
    console.log(`   OrbitControls enabled: ${this.controls.enabled}, target: (${this.modelCenter.x.toFixed(2)}, ${targetHeight.toFixed(2)}, ${this.modelCenter.z.toFixed(2)})`);
    
    // Create panorama hotspots
    this.createPanoramaHotspots();
    console.log(`   Created ${this.hotspots.length} panorama hotspots`);
    
    // Update UI
    this.updateUI();
    
    console.log('✅ Model view active');
  }

  /**
   * Exit model view
   */
  exit() {
    console.log('🏠 Exiting model view');
    console.log(`   isActive before: ${this.isActive}`);
    console.log(`   Hotspots to clear: ${this.hotspots.length}`);
    
    this.isActive = false;
    
    // Disable controls
    this.controls.enabled = false;
    console.log(`   OrbitControls disabled: ${this.controls.enabled}`);
    
    // Clear hotspots
    this.clearHotspots();
    console.log(`   Hotspots cleared, remaining: ${this.hotspots.length}`);
    
    console.log('✅ Model view exit complete');
  }

  /**
   * Update loop
   */
  update() {
    if (!this.isActive) return;
    
    this.controls.update();
  }

  /**
   * Create hotspot markers at each panorama position
   */
  createPanoramaHotspots() {
    this.clearHotspots();
    
    Object.entries(this.config.panoramas).forEach(([id, panoConfig]) => {
      const hotspot = this.createHotspotMarker(id, panoConfig);
      this.hotspots.push(hotspot);
      this.scene.add(hotspot);
    });
    
    console.log(`✅ Created ${this.hotspots.length} panorama hotspots in model view`);
  }

  /**
   * Create a single hotspot marker
   */
  createHotspotMarker(panoId, config) {
    const group = new THREE.Group();
    group.name = `modelHotspot_${panoId}`;
    
    // The calibration tool saves positions relative to the model's original position,
    // but ModelLoader repositions the model. We need to offset by the model's position.
    const offsetX = config.position.x + this.model.position.x;
    const offsetZ = config.position.z + this.model.position.z;
    
    // Place puck on the floor (Y=0) instead of at panorama height
    group.position.set(offsetX, 0, offsetZ);
    
    console.log(`📍 Pano ${panoId}: Config (${config.position.x.toFixed(2)}, ${config.position.z.toFixed(2)}) + Model offset (${this.model.position.x.toFixed(2)}, ${this.model.position.z.toFixed(2)}) = Final (${offsetX.toFixed(2)}, 0, ${offsetZ.toFixed(2)})`);
    
    // Create simple white ring puck (matching NavigationManager design)
    const ringGeometry = new THREE.RingGeometry(0.096, 0.144, 32); // Same size as panorama view pucks
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.45, // Same transparency as panorama view pucks
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: false
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.rotation.x = -Math.PI / 2; // Lay flat on floor
    ring.position.y = 0.01; // Slightly above floor to prevent z-fighting
    group.add(ring);
    
    // Create label sprite
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(0, 0, 256, 64);
    ctx.fillStyle = 'white';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(config.name || `Pano ${panoId}`, 128, 40);
    
    const texture = new THREE.CanvasTexture(canvas);
    const spriteMaterial = new THREE.SpriteMaterial({ 
      map: texture,
      transparent: true,
      depthTest: false
    });
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.scale.set(0.6, 0.15, 1);
    sprite.position.y = 0.3;
    sprite.visible = false; // Show on hover
    group.add(sprite);
    
    // Store metadata
    group.userData = {
      type: 'modelHotspot',
      panoId: panoId,
      panoName: config.name,
      ring: ring,
      label: sprite,
      originalOpacity: 0.45
    };
    
    // Add pulsing animation
    this.animateHotspot(group);
    
    return group;
  }

  /**
   * Animate hotspot (no pulsing, just billboard for label and ring rotation)
   */
  animateHotspot(hotspot) {
    hotspot.userData.animate = () => {
      // Billboard ring to always face camera
      if (hotspot.userData.ring && this.camera) {
        // Get direction from puck to camera
        const direction = new THREE.Vector3();
        direction.subVectors(this.camera.position, hotspot.position).normalize();
        
        // Calculate rotation to face camera
        const angle = Math.atan2(direction.x, direction.z);
        
        // Apply rotation to the ring
        hotspot.userData.ring.rotation.x = -Math.PI / 2; // Keep flat on floor
        hotspot.userData.ring.rotation.z = -angle; // Rotate to face camera
      }
      
      // Billboard label to always face camera
      if (hotspot.userData.label && this.camera) {
        hotspot.userData.label.lookAt(this.camera.position);
      }
    };
  }

  setupEventListeners() {
    this.renderer.domElement.addEventListener('mousedown', this.onMouseDown.bind(this));
    this.renderer.domElement.addEventListener('mouseup', this.onMouseUp.bind(this));
    this.renderer.domElement.addEventListener('mousemove', this.onMouseMove.bind(this));
  }
  
  onMouseDown(event) {
    if (!this.isActive) return;
    
    // Store initial mouse position
    this.mouseDownPosition.set(event.clientX, event.clientY);
    this.isDragging = false;
  }
  
  onMouseUp(event) {
    if (!this.isActive) {
      console.log('⚠️ ModelView mouseup ignored: not active');
      return;
    }
    
    // Calculate how far the mouse moved
    const mouseUpPosition = new THREE.Vector2(event.clientX, event.clientY);
    const dragDistance = this.mouseDownPosition.distanceTo(mouseUpPosition);
    
    // Only treat as a click if mouse didn't move much (threshold: 5 pixels)
    const DRAG_THRESHOLD = 5;
    
    if (dragDistance > DRAG_THRESHOLD) {
      console.log(`🖱️ Mouse dragged ${dragDistance.toFixed(0)}px - ignoring as click`);
      this.isDragging = true;
      return;
    }
    
    console.log(`🖱️ Click detected (drag distance: ${dragDistance.toFixed(0)}px)`);
    this.handleClick(event);
  }

  onMouseMove(event) {
    if (!this.isActive) return;
    
    // Update mouse position
    this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
    
    // Raycast to check for hotspot hover
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(this.hotspots, true);
    
    // Reset previously hovered hotspot
    if (this.hoveredHotspot) {
      this.unhighlightHotspot(this.hoveredHotspot);
      this.hoveredHotspot = null;
    }
    
    // Highlight new hotspot
    if (intersects.length > 0) {
      let hotspot = intersects[0].object;
      
      // Find parent hotspot group
      while (hotspot && !hotspot.userData.type) {
        hotspot = hotspot.parent;
      }
      
      if (hotspot && hotspot.userData.type === 'modelHotspot') {
        this.hoveredHotspot = hotspot;
        this.highlightHotspot(hotspot);
        this.renderer.domElement.style.cursor = 'pointer';
      }
    } else {
      this.renderer.domElement.style.cursor = 'default';
    }
  }

  handleClick(event) {
    // Compute normalized mouse coordinates
    const rect = this.renderer.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
  
    // Ray from camera
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.camera);
    const clickDir = raycaster.ray.direction.clone().normalize();
  
    // Find nearest panorama in click direction
    let nearestPanoId = null;
    let minAngle = Infinity;
    let minDistance = Infinity;
  
    for (const [panoId, panoConfig] of Object.entries(this.config.panoramas)) {
      const panoPos = new THREE.Vector3(
        panoConfig.position.x + this.model.position.x,
        panoConfig.position.y + this.model.position.y,
        panoConfig.position.z + this.model.position.z
      );
      const dirToPano = panoPos.clone().sub(this.camera.position).normalize();
      const angle = clickDir.angleTo(dirToPano);
      const distance = this.camera.position.distanceTo(panoPos);
  
      if (distance < minDistance || (distance === minDistance && angle < minAngle)) {
        minDistance = distance;
        minAngle = angle;
        nearestPanoId = panoId;
      }
    }
  
    if (!nearestPanoId) {
      console.warn('⚠️ No panorama found for click');
      return;
    }
  
    console.log(`🎯 Clicked anywhere → navigating to nearest panorama: ${nearestPanoId}`);
  
    if (this.onEnterPanoramaCallback) {
      // Keep horizontal camera direction
      let rotY = THREE.MathUtils.radToDeg(this.camera.rotation.y);
      rotY = ((rotY % 360) + 360) % 360;
  
      this.onEnterPanoramaCallback(nearestPanoId, {
        initialRotY: rotY,
        initialRotX: 0 // Keep camera level
      });
    } else {
      console.error('❌ No onEnterPanoramaCallback set!');
    }
  }
  

  highlightHotspot(hotspot) {
    if (!hotspot.userData.ring) return;
    
    // Scale up
    hotspot.scale.set(1.3, 1.3, 1.3);
    
    // Brighten
    hotspot.userData.ring.material.opacity = 0.75;
    
    // Show label
    if (hotspot.userData.label) {
      hotspot.userData.label.visible = true;
    }
  }

  unhighlightHotspot(hotspot) {
    if (!hotspot.userData.ring) return;
    
    // Scale back
    hotspot.scale.set(1.0, 1.0, 1.0);
    
    // Restore opacity
    hotspot.userData.ring.material.opacity = hotspot.userData.originalOpacity;
    
    // Hide label
    if (hotspot.userData.label) {
      hotspot.userData.label.visible = false;
    }
  }

  /**
   * Make model visible
   */
  makeModelVisible() {
    this.model.traverse((child) => {
      if (child.isMesh) {
        // Make mesh visible
        child.visible = true;
        
        if (child.userData.originalMaterial) {
          // Restore original material with textures (use reference, not clone)
          child.material = child.userData.originalMaterial;
          child.material.visible = true;
          child.material.colorWrite = true;
          child.material.depthWrite = true;
          child.material.depthTest = true;
          child.material.needsUpdate = true;
        } else {
          // Create default material if none exists
          child.material = new THREE.MeshStandardMaterial({
            color: 0xcccccc,
            roughness: 0.7,
            metalness: 0.3
          });
        }
        
        // Ensure proper rendering
        child.castShadow = false;
        child.receiveShadow = false;
      }
    });
    
    this.model.visible = true;
    console.log('   Model and all child meshes set to visible');
  }

  /**
   * Update UI
   */
  updateUI() {
    const locationElement = document.getElementById('current-location');
    const hintElement = document.getElementById('nav-hint');
    
    if (locationElement) {
      locationElement.textContent = 'Model View';
    }
    
    if (hintElement) {
      hintElement.textContent = 'Drag to rotate • Scroll to zoom • Click markers to enter';
    }
    
    // Update view button
    const viewLabel = document.getElementById('view-label');
    const viewIcon = document.getElementById('view-icon');
    if (viewLabel) viewLabel.textContent = 'Model View';
    if (viewIcon) viewIcon.textContent = '🏠';
  }

  /**
   * Clear all hotspots
   */
  clearHotspots() {
    this.hotspots.forEach(hotspot => {
      this.scene.remove(hotspot);
      
      // Dispose geometries and materials
      hotspot.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (child.material.map) child.material.map.dispose();
          child.material.dispose();
        }
      });
    });
    
    this.hotspots = [];
    this.hoveredHotspot = null;
  }

  /**
   * Set callback for entering panorama
   */
  onEnterPanorama(callback) {
    this.onEnterPanoramaCallback = callback;
  }

  /**
   * Clean up
   */
  dispose() {
    this.clearHotspots();
    this.controls.dispose();
  }
}

